import { useAtom } from "jotai"
import { ru } from "date-fns/locale"
import { useDayPicker } from "react-day-picker"
import { Calendar } from "~/components/ui/calendar"
import { useSwipe } from "~/hooks/useSwipe"
import { selectedDayAtom } from "./event-list"
import { EventDayButton } from "./event-day-button"
import type { DayInfo } from "./utils"

function SwipeRoot({ className, rootRef, ...props }: React.ComponentProps<"div"> & { rootRef?: React.Ref<HTMLDivElement> }) {
  const { nextMonth, previousMonth, goToMonth } = useDayPicker()
  const swipe = useSwipe({
    onLeft: () => nextMonth && goToMonth(nextMonth),
    onRight: () => previousMonth && goToMonth(previousMonth),
  })
  return <div data-slot="calendar" ref={rootRef} className={className} {...props} {...swipe} />
}

type Props = {
  dayMap: Map<string, DayInfo>
}

export function CalendarView({ dayMap }: Props) {
  const [selected, setSelected] = useAtom(selectedDayAtom)

  return (
    <Calendar
      mode="single"
      selected={selected}
      onSelect={(date) => date && setSelected(date)}
      weekStartsOn={1}
      locale={ru}
      className="w-full bg-white"
      formatters={{
        formatWeekdayName: (date) => {
          const text = date.toLocaleString("ru-RU", { weekday: "short" })
          return text.charAt(0).toUpperCase() + text.slice(1)
        },
        formatMonthCaption: (date) => {
          const text = date.toLocaleString("ru-RU", { month: "long" })
          return text.charAt(0).toUpperCase() + text.slice(1) + " " + date.getFullYear()
        },
      }}
      classNames={{
        caption_label: "select-none font-bold text-lg",
        month_caption: "flex items-center h-(--cell-size) w-full justify-start",
        nav: "flex items-center gap-1 absolute top-0 right-0",
        button_previous: "bg-white border border-border rounded-lg size-9 flex items-center justify-center hover:bg-muted",
        button_next: "bg-white border border-border rounded-lg size-9 flex items-center justify-center hover:bg-muted",
        weekday: "text-secondary-foreground rounded-md flex-1 text-[0.8rem] select-none",
        day: "relative w-full p-0 text-center [&:last-child[data-selected=true]_button]:rounded-r-md [&:first-child[data-selected=true]_button]:rounded-l-md group/day select-none h-cal-row",
        today: "outline outline-2 outline-primary outline-offset-[-2px] rounded-md",
        weekdays: "flex gap-1",
        week: "flex w-full mt-1 gap-1",
      }}
      components={{
        Root: SwipeRoot,
        DayButton: (props) => <EventDayButton dayMap={dayMap} {...props} />,
      }}
    />
  )
}
